// ============================================================
// Pure amortization-schedule builder — single source of truth for the
// prepaid run, projection, roll-forward report (apps/api) AND the web
// what-if simulator (apps/web). No I/O, no Date mutation; all math on
// decimal.js, all dates read in UTC (Prisma `@db.Date` serializes as
// UTC-midnight). Mirrors leases/lease-schedule-builder.
//
// Lives in @dcc-erp/shared so the Add-contract "what-if" preview computes
// the EXACT same schedule the backend will post (§9.7 — "reuses 3.1");
// apps/api re-exports it via a thin shim at
// modules/prepaid/amortization-schedule.ts so existing import sites are
// untouched.
// ============================================================
import Decimal from 'decimal.js';
const MS_PER_DAY = 86_400_000;
function utcMonthEnd(year, monthIndex0) {
    // Day 0 of next month === last day of this month.
    return new Date(Date.UTC(year, monthIndex0 + 1, 0));
}
function daysInMonthUTC(year, monthIndex0) {
    return utcMonthEnd(year, monthIndex0).getUTCDate();
}
// Inclusive day count (a 1-day window returns 1).
function inclusiveDays(start, end) {
    const s = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    const e = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
    return Math.round((e - s) / MS_PER_DAY) + 1;
}
// Inclusive month count anchored to (year, month) so day-of-month variations
// don't off-by-one. 2026-01-01..2026-12-31 -> 12; 2024-08-01..2026-07-31 -> 24.
export function monthsBetween(start, end) {
    const startMonths = start.getUTCFullYear() * 12 + start.getUTCMonth();
    const endMonths = end.getUTCFullYear() * 12 + end.getUTCMonth();
    return endMonths - startMonths + 1;
}
// Build the full straight-line schedule.
//   EVEN               - every period carries weight 1 -> total / months.
//   PRORATE_FIRST_LAST - each period's weight = (days of [start,end] within that
//                        calendar month) / (days in that month); full middle
//                        months weigh 1, the first/last partial months weigh < 1.
// In both modes the per-period amount is round(total * weight / Sigma-weight, 2)
// and the FINAL period absorbs the cumulative rounding residual so the sum of
// amounts === total exactly and the final closingBalance is 0.00.
export function buildAmortizationSchedule(total, start, end, method) {
    const months = monthsBetween(start, end);
    if (months < 1)
        return [];
    const totalDp = total.toDecimalPlaces(2);
    const startY = start.getUTCFullYear();
    const startM = start.getUTCMonth();
    // Per-period weights.
    const weights = [];
    for (let i = 0; i < months; i++) {
        const y = startY + Math.floor((startM + i) / 12);
        const m0 = (startM + i) % 12;
        if (method === 'EVEN') {
            weights.push(new Decimal(1));
            continue;
        }
        const dim = daysInMonthUTC(y, m0);
        const periodStart = i === 0 ? start : new Date(Date.UTC(y, m0, 1));
        const periodEnd = i === months - 1 ? end : utcMonthEnd(y, m0);
        weights.push(new Decimal(inclusiveDays(periodStart, periodEnd)).div(dim));
    }
    const weightSum = weights.reduce((s, w) => s.plus(w), new Decimal(0));
    const out = [];
    let posted = new Decimal(0);
    for (let i = 0; i < months; i++) {
        const y = startY + Math.floor((startM + i) / 12);
        const m0 = (startM + i) % 12;
        const isLast = i === months - 1;
        const raw = totalDp.times(weights[i]).div(weightSum);
        const amount = isLast ? totalDp.minus(posted) : raw.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
        const opening = totalDp.minus(posted);
        posted = posted.plus(amount);
        out.push({
            index: i + 1,
            period: `${y}${String(m0 + 1).padStart(2, '0')}`,
            voucherDate: utcMonthEnd(y, m0),
            amount,
            openingBalance: opening,
            closingBalance: opening.minus(amount),
        });
    }
    return out;
}
//# sourceMappingURL=amortization-schedule.js.map